import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { BrandGlyph } from '../components/brand-glyph.js';
import { Pill } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { api, type UserRole } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import {
  authMode,
  cognitoLogin,
  confirmPasswordReset,
  requestPasswordReset,
  type NewPasswordChallenge,
} from '../lib/cognito-client.js';
import { cn } from '../lib/cn.js';

/**
 * Post-login landing per role. Field techs go straight to their day list;
 * everyone else lands on the office home (Today). Mirrors the router-
 * level guard which would bounce a field tech off `/` anyway.
 */
function landingFor(role: UserRole): '/' | '/jobs' {
  return role === 'field_tech' ? '/jobs' : '/';
}

/**
 * Login entrypoint. Branches by Vite env:
 *
 *   VITE_AUTH_MODE=cognito → email/password form, Cognito SRP auth via
 *                            the user pool. On FORCE_CHANGE_PASSWORD the
 *                            form swaps to a "set new password" step.
 *                            "Forgot password?" runs the self-service
 *                            ForgotPassword flow (emailed code → new
 *                            password) without leaving the form.
 *   anything else (default) → dev IDP picker: select a seeded user, the
 *                             API issues a real RS256-signed JWT for it.
 *                             No password — dev only.
 */
export function LoginScreen() {
  if (authMode === 'cognito') return <CognitoLoginScreen />;
  return <DevLoginScreen />;
}

function CognitoLoginScreen() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  // Forgot-password flow: null = normal login; 'request' = asking for the
  // email; 'confirm' = code + new password. Mutually exclusive with
  // `challenge` — entering the flow requires the plain login form.
  const [resetStep, setResetStep] = useState<'request' | 'confirm' | null>(null);
  const [resetCode, setResetCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const exitReset = () => {
    setResetStep(null);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (resetStep === 'request') {
        await requestPasswordReset(email.trim().toLowerCase());
        setResetStep('confirm');
        return;
      }

      if (resetStep === 'confirm') {
        // Same client-side checks as the forced-change step.
        if (newPassword.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        if (newPassword !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await confirmPasswordReset(email.trim().toLowerCase(), resetCode.trim(), newPassword);
        exitReset();
        setPassword('');
        setNotice('Password updated — sign in with your new password.');
        return;
      }

      if (challenge) {
        // Second step — completing the forced password change.
        if (newPassword.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        if (newPassword !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        const session = await challenge.complete(newPassword);
        setSession(session);
        void navigate({ to: landingFor(session.user.role), replace: true });
        return;
      }

      // First step — authenticate. May resolve to a session (done!) or
      // a NewPasswordChallenge (Cognito demands a password change).
      const result = await cognitoLogin(email.trim().toLowerCase(), password);
      if ('kind' in result && result.kind === 'new-password-required') {
        setChallenge(result);
        setPassword(''); // clear the temp password from memory
      } else {
        setSession(result);
        void navigate({ to: landingFor(result.user.role), replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-12 space-y-5">
      <header className="text-center space-y-2">
        <div className="flex justify-center">
          <BrandGlyph size="lg" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Sign in to Tinsmith</h1>
        <p className="text-sm text-ink-muted">
          {challenge
            ? 'Pick a permanent password to finish setting up your account.'
            : resetStep === 'request'
              ? "Enter your email and we'll send you a reset code."
              : resetStep === 'confirm'
                ? 'Enter the code we emailed you, then pick a new password.'
                : 'Enter the email and password your admin provided.'}
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-3">
        {!challenge && resetStep !== 'confirm' && (
          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Email</span>
            <Input
              type="email"
              required
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </label>
        )}

        {!challenge && !resetStep && (
          <>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Password</span>
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </label>
            <div className="text-right">
              <button
                type="button"
                className="text-sm text-ink-muted hover:text-ink hover:underline"
                onClick={() => {
                  setNotice(null);
                  setError(null);
                  setResetStep('request');
                }}
                disabled={busy}
              >
                Forgot password?
              </button>
            </div>
          </>
        )}

        {resetStep === 'confirm' && (
          <>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Reset code</span>
              <Input
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                disabled={busy}
              />
              <span className="text-[10.5px] text-ink-subtle">
                Sent to {email.trim().toLowerCase()} — check spam if it doesn't arrive.
              </span>
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">New password</span>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={busy}
              />
              <span className="text-[10.5px] text-ink-subtle">
                At least 8 characters, with upper + lower + a number.
              </span>
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">
                Confirm password
              </span>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}

        {challenge && (
          <>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">New password</span>
              <Input
                type="password"
                required
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={busy}
              />
              <span className="text-[10.5px] text-ink-subtle">
                At least 8 characters, with upper + lower + a number.
              </span>
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">
                Confirm password
              </span>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}

        {notice && <Feedback variant="success">{notice}</Feedback>}
        {error && <Feedback variant="error">{error}</Feedback>}

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy
            ? resetStep === 'request'
              ? 'Sending…'
              : resetStep === 'confirm'
                ? 'Updating…'
                : 'Signing in…'
            : resetStep === 'request'
              ? 'Send reset code'
              : resetStep === 'confirm'
                ? 'Set new password'
                : challenge
                  ? 'Set password'
                  : 'Sign in'}
        </Button>

        {resetStep && (
          <div className="text-center">
            <button
              type="button"
              className="text-sm text-ink-muted hover:text-ink hover:underline"
              onClick={exitReset}
              disabled={busy}
            >
              Back to sign in
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function DevLoginScreen() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [selected, setSelected] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['dev-auth', 'candidates'],
    queryFn: ({ signal }) => api.auth.candidates(signal),
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (email: string) => api.auth.login(email),
    onSuccess: (res) => {
      setSession({
        token: res.accessToken,
        expiresIn: res.expiresIn,
        user: {
          id: res.user.id,
          email: res.user.email,
          displayName: res.user.displayName,
          role: res.user.role,
        },
      });
      void navigate({ to: landingFor(res.user.role) });
    },
  });

  const candidates = usersQuery.data ?? [];

  return (
    <div className="mx-auto max-w-md py-8 space-y-5">
      <header className="text-center space-y-2">
        <div className="flex justify-center">
          <BrandGlyph size="lg" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Sign in to Tinsmith</h1>
        <p className="text-sm text-ink-muted">
          Pick a seeded user to continue. The dev IDP issues a real RS256-signed JWT — production
          replaces this picker with the Cognito email/password form.
        </p>
      </header>

      <div
        className="rounded-md border p-2.5 text-xs flex items-start gap-2"
        style={{
          borderColor: 'var(--color-border-tertiary)',
          backgroundColor: 'var(--color-customer-card-bg)',
          color: 'var(--color-customer-card-accent)',
        }}
      >
        <ShieldOff className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
        <span>
          Dev login. To skip auth entirely, restart the API with{' '}
          <span className="font-mono">AUTH_DISABLED=true</span>.
        </span>
      </div>

      {usersQuery.isPending && (
        <ul className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="h-14 bg-surface-muted rounded animate-pulse" />
          ))}
        </ul>
      )}

      {usersQuery.isError && (
        <Feedback variant="error" size="lg">
          Couldn't reach the dev IDP: {usersQuery.error.message}
        </Feedback>
      )}

      {usersQuery.data && candidates.length === 0 && (
        <div className="text-sm text-ink-muted text-center">
          No active users found. Add one in <span className="font-mono">/admin → Users</span>.
        </div>
      )}

      <ul className="space-y-2">
        {candidates.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => {
                setSelected(u.email);
                loginMutation.mutate(u.email);
              }}
              disabled={loginMutation.isPending}
              className={cn(
                'w-full grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border bg-surface px-3 py-3 text-left transition-shadow hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed',
              )}
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <Avatar name={u.displayName} />
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink truncate">{u.displayName}</div>
                <div className="text-xs font-mono text-ink-muted truncate">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill variant={ROLE_VARIANT[u.role]}>{ROLE_LABEL[u.role]}</Pill>
                {selected === u.email && loginMutation.isPending && (
                  <span className="text-[10px] text-ink-muted">Signing in…</span>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {loginMutation.isError && (
        <Feedback variant="error" size="lg">
          Login failed: {loginMutation.error.message}
        </Feedback>
      )}
    </div>
  );
}

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  office: 'Office',
  field_tech: 'Field tech',
};

const ROLE_VARIANT: Record<UserRole, 'install' | 'service' | 'pm' | 'sales'> = {
  admin: 'install',
  dispatcher: 'service',
  office: 'pm',
  field_tech: 'sales',
};

function Avatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  return (
    <span
      aria-hidden
      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
      style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
    >
      {initials}
    </span>
  );
}
